import { Directive, ElementRef, Renderer2, input, InputSignal, effect, inject } from '@angular/core';
import { TsColorUtils } from '../../utils/ts-color.utils';

@Directive({
  selector: '[TsFieldStyling]',
  standalone: true,
})
export class FieldStylingDirective {
  readonly fieldBackgroundColor: InputSignal<string> = input.required();
  readonly fieldBold: InputSignal<boolean> = input.required();
  /*
   * The maximum recursion depth for the background color application.
   *
   * By default we go 10 levels deeps coloring the background of the element and its children,
   * but sometimes we might color elements that are too deep in the DOM tree, and shouldn't be colored.
   *
   * This property aims to limit that.
   */
  readonly maxElementColoringRecursionDepth: InputSignal<number> = input(10);
  readonly targetStylingClass: InputSignal<string> = input(null);
  readonly targetTextClass: InputSignal<string> = input(null);
  readonly defaultBackgroundColor: InputSignal<string> = input('#FFFFFF');

  // allow users to target a specific class
  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly changesRef = effect(() => {
    if (this.targetStylingClass()) {
      this.applyFieldStyles(this.specificTargetStylingElement, 1);
    } else {
      this.applyFieldStyles(this.el.nativeElement, this.maxElementColoringRecursionDepth());
    }
  });

  private get specificTargetStylingElement(): HTMLElement {
    return this.el.nativeElement.querySelector(this.targetStylingClass());
  }

  private get specificTargetTextElement(): HTMLElement {
    return this.el.nativeElement.querySelector(this.targetTextClass());
  }

  private applyFieldStyles(element: HTMLElement, depth: number) {
    if (this.fieldBackgroundColor()) {
      this.applyBackgroundColorRecursively(element, this.fieldBackgroundColor(), depth);
    } else {
      this.applyBackgroundColorRecursively(element, this.defaultBackgroundColor(), depth);
    }

    if (this.fieldBold()) {
      this.renderer.addClass(element, 'font-bold');
    } else {
      this.renderer.removeClass(element, 'font-bold');
    }
  }

  private applyBackgroundColorRecursively(element: HTMLElement, color: string, depth: number) {
    if (depth <= 0 || !element) return;

    this.renderer.setStyle(element, 'background-color', color);

    if (TsColorUtils.isContrastLight(color)) {
      this.renderer.addClass(this.specificTargetTextElement ?? element, 'text-white');
    } else {
      this.renderer.removeClass(this.specificTargetTextElement ?? element, 'text-white');
    }

    Array.from(element.children).forEach((child) => {
      this.applyBackgroundColorRecursively(child as HTMLElement, color, depth - 1);
    });
  }
}
